<template>
    <AppHeader fixed>
        <SidebarToggler class="d-lg-none" display="md" mobile />
        <b-link class="navbar-brand" :to="`/v2/profile/${currentUser.id}`">
            <img class="navbar-brand-full" :src="logo" width="89" height="25" alt="CoBlue Logo" />
            <img class="navbar-brand-minimized" :src="sygnet" width="30" height="30" alt="CoBlue Logo" />
        </b-link>
        <SidebarToggler class="d-none d-md-down-none" display="lg" :defaultOpen="false" />
        <b-navbar-nav class="d-md-down-none">
            <b-nav-item class="px-3" :to="`/v2/profile/${currentUser.id}`"> Home </b-nav-item>
            <b-nav-item class="px-3">
                <PeopleDropdown :currentUser="currentUser" />
            </b-nav-item>
            <b-nav-item
                v-if="
                    currentUserPermissions.hasStrategy &&
                    (currentUserPermissions.isAdmin ||
                        currentUserPermissions.isLocalAdministrator ||
                        currentUserPermissions.isGod ||
                        (currentUserPermissions.isUser && currentUserPermissions.strategyViewer))
                "
                class="px-3"
            >
                <StrategyDropdown
                    :has-strategy="currentUserPermissions.hasStrategy"
                    :is-admin="currentUserPermissions.isAdmin"
                    :is-local-administrator="currentUserPermissions.isLocalAdministrator"
                    :isGod="currentUserPermissions.isGod"
                    :isUser="currentUserPermissions.isUser"
                    :strategyViewer="currentUserPermissions.strategyViewer"
                />
            </b-nav-item>
            <b-nav-item class="px-3" to="/v2/target" v-if="showTargetModule">
                <TargetDropdown
                    :has-kpi="hasKPI"
                    :has-target="hasTarget"
                    :is-admin="currentUserPermissions.isAdmin"
                    :is-local-administrator="currentUserPermissions.isLocalAdministrator"
                    :is-god="currentUserPermissions.isGod"
                    :is-target-master="currentUserPermissions.isTargetMaster"
                />
            </b-nav-item>
            <b-nav-item class="px-3" to="/okr" v-if="currentUserPermissions.hasOkr && currentUser.company.has_okr">
                <OkrDropdown
                    :is-admin="currentUserPermissions.isAdmin"
                    :is-local-administrator="currentUserPermissions.isLocalAdministrator"
                    :is-okr-leader="currentUserPermissions.isOkrLeader"
                    :is-god="currentUserPermissions.isGod"
                    :hasDaily="currentUserPermissions.hasDaily"
                    :currentUser="currentUser"
                />
            </b-nav-item>
            <b-nav-item
                class="px-3"
                to="/v2/talk"
                v-if="currentUserPermissions.hasTalk && currentUser.company.has_talk"
            >
                <TalkDropdown
                    :is-admin="currentUserPermissions.isAdmin"
                    :has-talk="currentUserPermissions.hasTalk"
                    :is-values-master="currentUserPermissions.isValuesMaster"
                    :isGod="currentUserPermissions.isGod"
                />
            </b-nav-item>
            <b-nav-item
                class="px-3"
                to="/v2/values"
                v-if="currentUserPermissions.hasValues && currentUser.company.has_value"
            >
                <ValuesDropdown
                    :evaluations="pendingEvaluations"
                    :is-god="currentUserPermissions.isGod"
                    :is-admin="currentUserPermissions.isAdmin"
                    :has-values="currentUserPermissions.hasValues"
                    :is-values-master="currentUserPermissions.isValuesMaster"
                    :is-local-administrator="currentUserPermissions.isLocalAdministrator"
                    :current-user-id="currentUser.id"
                />
            </b-nav-item>
            <b-nav-item class="px-3" to="/v2/reports">
                <ReportsDropdown :permissions="currentUserPermissions" />
            </b-nav-item>
            <b-nav-item class="px-3" to="/v2/administrator" v-if="showAdministratorMenu">
                <AdministrationDropdown
                    :current-user-permissions="currentUserPermissions"
                    :company-id="currentUser.company_id"
                />
            </b-nav-item>
        </b-navbar-nav>
        <b-navbar-nav class="nav-bar-right text-muted">
            <a
                id="btn-nav-bar-coblue-store"
                href="https://coblue.com.br/store/"
                target="_blank"
                class="d-md-down-none text-muted"
                title="CoBlue Store"
            >
                <i class="nav-icon fa fa-shopping-cart"></i>
            </a>
            <a
                id="btn-nav-bar-help-center"
                class="d-md-down-none"
                href="https://coblue.movidesk.com/kb"
                target="_blank"
                :title="$t('header.help')"
            >
                <i class="nav-icon fas fa-question-circle text-muted"></i>
            </a>
            <a class="d-md-down-none">
                <notifications />
            </a>
            <HeaderDropdown :current-user="currentUserInfos" class="d-md-down-none ml-2" />
        </b-navbar-nav>
        <AsideToggler class="d-none" />
    </AppHeader>
</template>
<script>
import { Header as AppHeader, SidebarToggler, AsideToggler } from '@coreui/vue'
import api from '@/services/api'
import Notifications from '@/views/notifications'
import { HeaderDropdown as AppHeaderDropdown } from '@coreui/vue'
import HomeDropdown from './Dropdowns/HomeDropdown'
import OkrDropdown from './Dropdowns/OkrDropdown'
import TalkDropdown from './Dropdowns/TalkDropdown'
import ValuesDropdown from './Dropdowns/ValuesDropdown'
import HeaderDropdown from './Dropdowns/HeaderDropdown'
import PeopleDropdown from './Dropdowns/PeopleDropdown'
import StrategyDropdown from './Dropdowns/StrategyDropdown'
import TargetDropdown from './Dropdowns/TargetDropdown'
import ReportsDropdown from './Dropdowns/ReportsDropdown'
import AdministrationDropdown from './Dropdowns/AdministrationDropdown'
import { userPermissionsMixin } from '@/mixins'
import { EventBuzz } from '@/main'
import logo from '/public/img/brand/logo.svg'
import sygnet from '/public/img/brand/sygnet.svg'

export default {
    name: 'NavBar',
    mixins: [userPermissionsMixin],
    components: {
        AppHeader,
        AsideToggler,
        Notifications,
        SidebarToggler,
        AppHeaderDropdown,
        OkrDropdown,
        HomeDropdown,
        TalkDropdown,
        ValuesDropdown,
        PeopleDropdown,
        StrategyDropdown,
        HeaderDropdown,
        TargetDropdown,
        ReportsDropdown,
        AdministrationDropdown,
    },
    props: {
        currentUser: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            pendingEvaluations: null,
            logo: logo,
            sygnet: sygnet,
        }
    },
    created() {
        EventBuzz.$on('evaluation-sented', this.getPendingEvaluations)
    },
    mounted() {
        this.getPendingEvaluations()
    },
    methods: {
        getPendingEvaluations() {
            if (this.currentUser.has_value && this.currentUser.company.has_value) {
                return api
                    .get('/evaluations', {
                        params: {
                            filter: 'id',
                            search: `valuer_id:${this.currentUser.id};status:pending`,
                            and: 'yes',
                        },
                    })
                    .then((resp) => {
                        const {
                            data: { data },
                        } = resp
                        this.pendingEvaluations = data.length
                    })
                    .catch((err) => console.error(err))
            }
        },
    },
    computed: {
        currentUserPermissions() {
            const {
                company,
                has_kpi,
                has_okr,
                has_talk,
                has_value,
                has_goal,
                is_manager,
                is_allowed_to_access_manager_profile,
                permissions,
                role,
                is_values_master,
                is_target_master,
                has_360_visualization,
                has_admin_access,
                is_okr_leader,
                strategy_viewer,
                has_modules,
            } = this.currentUser
            return {
                hasDaily: company.has_daily,
                hasStrategy: company.has_strategy,
                hasKpi: has_kpi,
                hasOkr: has_okr,
                hasTalk: has_talk,
                hasValues: has_value,
                hasTarget: has_goal,
                isUser: role.name === 'user',
                isAdmin: role.name === 'administrator',
                isGod: role.name === 'god',
                isManager: is_manager,
                isLocalAdministrator: role.name === 'local_administrator',
                isOkrLeader: is_okr_leader,
                isAllowedToAccessManagerProfile: is_allowed_to_access_manager_profile,
                isValuesMaster: is_values_master,
                isTargetMaster: is_target_master,
                has360Visualization: has_360_visualization,
                hasAdminAccess: has_admin_access,
                permissions: {
                    section: permissions.section,
                    user: permissions.user,
                    period: permissions.period,
                    company: permissions.company,
                    role,
                },
                modules: has_modules,
                eventPermissions: this.currentUser.permissions.goals,
                strategyViewer: strategy_viewer,
            }
        },
        currentUserInfos() {
            const { id, name, email, picture } = this.currentUser
            return { id, name, email, picture }
        },
        showTargetModule() {
            return (
                (this.currentUserPermissions.hasTarget && this.currentUser.company.has_goal) ||
                (this.currentUserPermissions.hasKpi && this.currentUser.company.has_kpi)
            )
        },
        hasTarget() {
            return this.currentUserPermissions.hasTarget && this.currentUser.company.has_goal
        },
        hasKPI() {
            return this.currentUserPermissions.hasKpi && this.currentUser.company.has_kpi
        },
        showAdministratorMenu() {
            const availableRoles = ['god', 'administrator', 'local_administrator', 'has_admin_access']
            const rolesAndModulesPermission = this.userHasPermission(this.currentUser, availableRoles, [])
            const customRules = this.currentUser.has_modules.includes('has_value') && this.currentUser.is_values_master
            return rolesAndModulesPermission || customRules
        },
    },
}
</script>
<style lang="scss" scoped>
.nav-bar-right {
    margin-left: auto;

    a {
        padding: 0.375rem 0.6rem;
        cursor: pointer;
    }
}
</style>
